<template>
  <div class="sections-container h-full">
    <!-- Drag and Drop Layout (when enabled) -->
    <template v-if="launchDarkly.enable_selectors_dnd">
      <NGrid class="h-full" cols="1" item-responsive responsive="screen">
        <NGridItem class="pa-2" span="1">
          <div ref="toolbarWrapper" class="sticky z-100">
            <template v-if="!shouldShowSmartFolderView">
              <div class="flex items-start gap-2">
                <CSearchFilters
                  v-model:filters="filters"
                  class="flex-1"
                  :disabled="!!isFetchingSections"
                  entity="canvas"
                  is-section
                  :placeholder="t('canvasUI.canvasSelector.sections.searchByNameOrTag')"
                  v-bind="popularityProps"
                  @update:filters="fetchSectionsAfterFiltering"
                  @update:search="handleSearch"
                />
                <CButton
                  v-if="showSmartFolderToggle"
                  v-tooltip="t('canvasUI.components.sections.CSectionManagementToolbar.smartFolders')"
                  circle
                  :disabled="!!isFetchingSections"
                  icon
                  @click="toggleSmartFoldersView"
                >
                  <CIcon icon="folder-bookmark" size="16px" />
                </CButton>
              </div>

              <SectionSelectorToolbar
                :added-item-ids="addedSections.map((s) => s.id)"
                :are-all-selected="areAllSelected"
                :are-some-selected="areSomeSelected"
                :current-page="currentPage"
                :disable-pagination="enablePipeMultisearch"
                :first-page-total-displayed-items="firstPageTotalDisplayedSections ?? 0"
                :loading="isFetchingSections"
                :page-size="orderedAvailableSections?.length > PAGE_SIZE ? orderedAvailableSections?.length : PAGE_SIZE"
                :selected-item-ids="localSelectedSections.map((s) => s.id)"
                :total-items="availableSectionsTotalCount"
                :total-selected="localSelectedSections?.length ?? 0"
                @clear-selection="localSelectedSections = []"
                @items-added="addItems"
                @items-removed="removeItems"
                @page-first="fetchFirstChunk"
                @page-last="fetchLastChunk"
                @page-next="fetchNextChunk"
                @page-previous="fetchPreviousChunk"
                @toggle-select-all="toggleSelectAll"
              />
            </template>
          </div>

          <template v-if="shouldShowSmartFolderView">
            <CSectionSmartFolders
              ref="smartFoldersRef"
              :added-section-ids="addedSections.map((s) => s.id)"
              mode="selector"
              :selected-section-ids="localSelectedSections.map((s) => s.id)"
              storage-key="section-selector"
              @clear-selection="localSelectedSections = []"
              @section-add="handleSmartFolderSectionAdd"
              @section-remove="handleSmartFolderSectionRemove"
              @section-select="handleSmartFolderSectionSelect"
              @sections-bulk-add="handleSmartFolderBulkAdd"
              @sections-bulk-remove="handleSmartFolderBulkRemove"
            />
          </template>
          <template v-else>
            <div v-if="isFetchingSections" class="pt-3 overflow-auto" :style="{ height: wrapperHeight }">
              <NSkeleton v-for="n in 8" :key="n" class="w-full rounded" :class="{ 'mt-1': n > 1 }" :height="95" />
            </div>
            <CList
              v-else
              class="rounded-b-lg flex bg-base overflow-y-auto"
              :data="orderedAvailableSections"
              :height="parseInt(wrapperHeight)"
              :maxRowHeight="200"
              movable
              :selectedIds="localSelectedSections.map((s) => s.id)"
              @item:click="onSectionSelect"
            >
              <template #item="{ item, index, isSelected }">
                <SectionSelectorItem
                  :data="orderedAvailableSections as CanvasRetrieve[]"
                  :filters="filters"
                  :has-sequence="sequenceEnabled"
                  :index="index"
                  :is-selected="isSelected"
                  :item="item"
                  :selected-sections="localSelectedSections as CanvasRetrieve[]"
                >
                  <template #actions>
                    <CButton
                      circle
                      class="h-9 w-9"
                      :class="{
                        'bg-base !focus:bg-base !active:bg-base': !isSelected && !isAdded(getTypedItem(item)),
                        'bg-primary5': isSelected,
                        'bg-error2 !focus:bg-error2 !active:bg-error2 ': isAdded(getTypedItem(item)),
                      }"
                      secondary
                      type="default"
                      @click.stop="handleItemClick(getTypedItem(item))"
                    >
                      <template #icon>
                        <CIcon v-if="isSelected" class="ml-[2px] color-primary" icon="grip-dots-vertical" size="12" />
                        <CIcon
                          v-else-if="isAdded(getTypedItem(item))"
                          class="ml-[2px] color-error"
                          icon="times"
                          size="12"
                        />
                        <CIcon v-else class="ml-[2px] color-primary" icon="plus" size="12" />
                      </template>
                    </CButton>
                  </template>
                </SectionSelectorItem>
              </template>
            </CList>
          </template>
        </NGridItem>
      </NGrid>
    </template>

    <!-- Traditional Layout (when drag-and-drop is disabled) -->
    <template v-else>
      <div ref="toolbarWrapperTraditional" class="sticky z-100">
        <CSearchFilters
          v-model:filters="filters"
          :disabled="!!isFetchingSections"
          entity="canvas"
          is-section
          :placeholder="t('canvasUI.canvasSelector.sections.searchByNameOrTag')"
          v-bind="popularityProps"
          @update:filters="fetchSectionsAfterFiltering"
          @update:search="handleSearch"
        />

        <SectionSelectorToolbar
          :are-all-selected="areAllSelected"
          :are-some-selected="areSomeSelected"
          :current-page="currentPage"
          :first-page-total-displayed-items="firstPageTotalDisplayedSections ?? 0"
          :loading="isFetchingSections"
          :page-size="orderedAvailableSections?.length > PAGE_SIZE ? orderedAvailableSections?.length : PAGE_SIZE"
          :total-items="availableSectionsTotalCount"
          :total-selected="selectedSections?.length ?? 0"
          @page-first="fetchFirstChunk"
          @page-last="fetchLastChunk"
          @page-next="fetchNextChunk"
          @page-previous="fetchPreviousChunk"
          @toggle-select-all="toggleSelectAll"
        />
      </div>
      <div v-if="isFetchingSections" class="pt-3 overflow-auto" :style="{ height: wrapperHeightTraditional }">
        <NSkeleton v-for="n in 8" :key="n" class="w-full rounded" :class="{ 'mt-1': n > 1 }" :height="135" />
      </div>
      <CList
        v-else
        class="rounded-2 flex bg-base overflow-y-auto"
        :data="orderedAvailableSections"
        :height="parseInt(wrapperHeightTraditional)"
        :maxRowHeight="200"
        :movable="false"
        :selectedIds="selectedSections.map((s) => s.id)"
        @item:click="onSectionSelect"
      >
        <template #item="{ item, index, isSelected }">
          <CanvasListItem
            :data="orderedAvailableSections"
            :filters="filters"
            :index="index"
            :is-selected="isSelected"
            :item="item"
            :section="item"
            :selected-items="selectedSections"
          />
        </template>
      </CList>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { AxiosInstance } from 'axios'
import { intersection, orderBy, sortBy, unionBy, uniq } from 'lodash-es'
import { NSkeleton, NGrid, NGridItem } from 'naive-ui'
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type ComputedRef,
  type Ref,
  type ToRefs,
} from 'vue'
import { useI18n } from 'vue-i18n'

import { fetchSectionsWithPipeMultiSearch } from '@canvas-builder/components/ui/SectionList/SectionSelector.helpers'
import SectionSelectorToolbar from '@canvas-builder/components/ui/SectionList/SectionSelectorToolbar.vue'
import SectionSelectorItem from '@canvas-builder/components/ui/SectionList/SectionSelectorItem.vue'
import CSectionSmartFolders from '@lib/components/CSectionSmartFolders/CSectionSmartFolders.vue'
import { EntityType, usePopularity } from '@canvas-builder/composables/usePopularity'
import useRecommendations from '@canvas-builder/composables/useRecommendations'
import useSectionPopularity from '@canvas-builder/composables/useSectionPopularity'
import useSectionSmartFolderStore from '@lib/stores/sectionSmartFolderStore'
import type { AppProps } from '@canvas-selector/appTypes'
import useAppStore from '@canvas-selector/stores/app'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CList from '@lib/components/CList/CList.vue'
import CSearchFilters from '@lib/components/CSearchFilters/CSearchFilters.vue'
import { normalizeFilterParams } from '@lib/components/filters/filter.util'
import { useElementHeightResize } from '@lib/composables/useElementHeightResize'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { PaginatedData } from '@lib/types/paginatedData'
import { urlSafeFetchInChunks } from '@lib/util/network'
import type { Canvas, CanvasRetrieve } from '@openapi'
import CanvasListItem from './CanvasListItem.vue'

const appProps = inject<ToRefs<AppProps>>('appProps')
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const PAGE_SIZE = computed(() => launchDarkly.value.canvas_section_selector_page_size || 500)
const filters = ref<Record<string, any>>({})

const { selectedSections, selectedItems } = useAppStore()

// Drag and drop specific variables
// addedSections is a computed that filters sections from selectedItems
// This ensures we work with a single source of truth
const addedSections = computed(
  () => selectedItems.value.filter((item) => item.is_section && !item.is_template) as CanvasRetrieve[],
)
const localSelectedSections = ref<CanvasRetrieve[]>([])
const smartFoldersRef = ref<InstanceType<typeof CSectionSmartFolders> | null>(null)

const toolbarWrapper = ref<HTMLElement | null>(null)
const toolbarWrapperTraditional = ref<HTMLElement | null>(null)

const { engagingSections, getOrderingConfiguration, getSystemFilterSectionIds, popularSections, getDefaultOrdering } =
  useSectionPopularity(computed(() => filters.value))
const { fetchPopularityData } = usePopularity()
const { t } = useI18n()
const search = ref('')
const availableSections = ref<CanvasRetrieve[] | Canvas[]>([])
const availableSectionsTotalCount = ref(0)
const isFetchingSections = ref(false)
const isFetchingSelectedSections = ref(false)
const currentPage = ref(1)
const firstPageTotalDisplayedSections = ref<number | undefined>()
const { wrapperHeight } = useElementHeightResize(toolbarWrapper, calculateWrapperHeight)
const { wrapperHeight: wrapperHeightTraditional } = useElementHeightResize(
  toolbarWrapperTraditional,
  calculateWrapperHeight,
)
const { recommendedSectionIds } = useRecommendations()
const restApiAxios = inject<Ref<AxiosInstance | undefined>>('restApiAxios')
const { isSmartFoldersView } = useSectionSmartFolderStore()

const popularityProps = computed(() => ({
  engagingIds: launchDarkly.value.show_popularity_icons ? engagingSections.value.map((item) => item.id) : undefined,
  popularIds: launchDarkly.value.show_popularity_icons ? popularSections.value.map((item) => item.id) : undefined,
  recommendedIds: recommendedSectionIds.value ?? [],
  showLatestFilter: true,
}))

const enablePipeMultisearch = computed(() => !!(launchDarkly.value.enable_pipe_character_multi_search && search.value))
const pipeSearchTerms = computed(() =>
  uniq(
    search.value
      .split(pitcherSettings.value?.pipe_multi_search_separator || '|')
      .map((term) => term.trim())
      .filter((term) => term),
  ),
)
const isPipeSearching = computed(() => pipeSearchTerms.value.length > 1)
const orderedAvailableSections = computed(() => {
  if (enablePipeMultisearch.value && isPipeSearching.value) {
    return availableSections.value
  }

  const sorters = getOrderingConfiguration({
    sortByMetadata: true,
    sequencingKey: appProps?.sequencingKey?.value,
  })

  return orderBy(
    availableSections.value,
    sorters.map(([sorter]) => sorter),
    sorters.map(([, order]) => order),
  ) as CanvasRetrieve[]
})

const sequenceEnabled = computed(() => {
  return orderedAvailableSections.value.some((item) => item.metadata?.sequence)
})

const isSmartFoldersEnabled = computed(
  () =>
    launchDarkly.value.enable_smart_folders_for_sections && launchDarkly.value.enable_smart_folders_in_section_selector,
)

const shouldShowSmartFolderView = computed(() => isSmartFoldersEnabled.value && isSmartFoldersView.value)

const showSmartFolderToggle = computed(() => isSmartFoldersEnabled.value)

const areAllSelected = computed(() => {
  const currentSelected = launchDarkly.value.enable_selectors_dnd ? localSelectedSections.value : selectedSections.value
  return availableSections.value?.every((section) => currentSelected.some((s) => s.id === section.id))
})

const areSomeSelected = computed(() => {
  const currentSelected = launchDarkly.value.enable_selectors_dnd ? localSelectedSections.value : selectedSections.value
  return (
    availableSections.value?.some((section) => currentSelected.some((s) => s.id === section.id)) &&
    !areAllSelected.value
  )
})

function calculateWrapperHeight(height: number) {
  // Handle different container structures for drag-and-drop vs traditional modes
  if (launchDarkly.value.enable_selectors_dnd) {
    // Drag-and-drop mode: uses NGridItem container
    const container = toolbarWrapper.value?.parentElement
    if (!container) return '400px' // fallback height

    const containerHeight = container.offsetHeight
    const toolbarHeight = height || 0
    const padding = 32 // Account for padding and margins

    const calculatedHeight = containerHeight - toolbarHeight - padding
    return `${Math.max(calculatedHeight, 200)}px` // Minimum height of 200px
  } else {
    // Traditional mode: uses direct parent container (sections-container)
    const toolbar = toolbarWrapperTraditional.value || toolbarWrapper.value
    const sectionsContainer = toolbar?.parentElement // This should be sections-container
    if (!sectionsContainer) return '400px' // fallback height

    const containerHeight = sectionsContainer.offsetHeight
    const toolbarHeight = height || 0
    const padding = 16 // Less padding needed for traditional layout

    const calculatedHeight = containerHeight - toolbarHeight - padding
    return `${Math.max(calculatedHeight, 200)}px` // Minimum height of 200px
  }
}

function onSectionSelect(section: CanvasRetrieve) {
  if (launchDarkly.value.enable_selectors_dnd) {
    // For drag-and-drop mode, handle selection differently
    const elementExists = localSelectedSections.value.find((s) => s.id === section.id)
    if (elementExists) {
      localSelectedSections.value = localSelectedSections.value.filter((s) => s.id !== section.id)
    } else {
      localSelectedSections.value.push(section)
    }
  } else {
    // Original behavior for non-drag-and-drop mode
    const elementExists = selectedItems.value.find((s) => s.id === section.id)
    if (elementExists) selectedItems.value = selectedItems.value.filter((s) => s.id !== section.id)
    else selectedItems.value.push(section)
  }
}

// Drag and drop specific functions
function getTypedItem(item: any) {
  return item as CanvasRetrieve
}

function isAdded(item: CanvasRetrieve) {
  return addedSections.value.some((s) => s.id === item.id)
}

function handleItemClick(item: CanvasRetrieve) {
  if (isAdded(item)) {
    removeItems([item.id])
  } else {
    addItems([item.id])
  }
}

function onDrop(dropOnItemId?: Canvas['id'], index?: number) {
  addItems(
    localSelectedSections.value.map((i) => i.id),
    'bottom',
    index,
  )
}

function selectChunk() {
  if (launchDarkly.value.enable_selectors_dnd) {
    localSelectedSections.value = [
      ...localSelectedSections.value,
      ...(orderedAvailableSections.value.filter(
        (s) => !localSelectedSections.value.some((selectedSection) => selectedSection.id === s.id),
      ) as CanvasRetrieve[]),
    ]
  } else {
    selectedItems.value = [
      ...selectedSections.value,
      ...(orderedAvailableSections.value.filter(
        (s) => !selectedSections.value.some((selectedSection) => selectedSection.id === s.id),
      ) ?? []),
    ]
  }
}

function addItems(itemIds: CanvasRetrieve['id'][], position?: 'top' | 'bottom', targetIndex?: number) {
  // Get items that are already in selectedItems (being moved)
  const itemsToMove = addedSections.value.filter((section) => itemIds.includes(section.id))

  // Get new items to add from availableSections
  let newItemsToAdd = availableSections.value.filter(
    (s) => itemIds.includes(s.id) && !itemsToMove.some((m) => m.id === s.id),
  )

  // If we're in smart folders view and some items weren't found in availableSections,
  // try to get them from smart folders
  if (shouldShowSmartFolderView.value && smartFoldersRef.value) {
    const smartFolderSections = smartFoldersRef.value.getSelectedSections()
    const missingIds = itemIds.filter(
      (id) => !newItemsToAdd.some((item) => item.id === id) && !itemsToMove.some((item) => item.id === id),
    )

    const smartFolderItemsToAdd = smartFolderSections.filter((section) => missingIds.includes(section.id))
    newItemsToAdd = [...newItemsToAdd, ...smartFolderItemsToAdd]
  }

  if (targetIndex === undefined && newItemsToAdd.length === 0 && itemsToMove.length === 0) {
    return
  }

  // Collect all items being added/moved
  let itemsToAdd = itemIds
    .map((id) => itemsToMove.find((item) => item.id === id) || newItemsToAdd.find((item) => item.id === id))
    .filter((item): item is CanvasRetrieve => item !== undefined)

  // Sort itemsToAdd based on their order in orderedAvailableSections if sequenceEnabled is true
  if (sequenceEnabled.value) {
    itemsToAdd = sortBy(itemsToAdd, (item) =>
      orderedAvailableSections.value.findIndex((section) => section.id === item.id),
    )
  }

  // Work directly with selectedItems - remove the sections being added/moved
  const remainingItems = selectedItems.value.filter((item) => !itemIds.includes(item.id))

  // Calculate where to insert based on position or targetIndex
  // targetIndex is relative to the FULL selectedItems array (including non-sections)
  let insertIndex: number

  if (position === 'top') {
    // Insert at the beginning
    insertIndex = 0
  } else if (targetIndex !== undefined) {
    // targetIndex is from the full array, so we need to adjust it
    // Count how many items we're removing that are before targetIndex
    let adjustedIndex = targetIndex
    for (let i = 0; i < targetIndex && i < selectedItems.value.length; i++) {
      if (itemIds.includes(selectedItems.value[i].id)) {
        adjustedIndex--
      }
    }
    insertIndex = Math.max(0, adjustedIndex)
  } else {
    // Append to the end
    insertIndex = remainingItems.length
  }

  // Insert items at the calculated position
  selectedItems.value = [...remainingItems.slice(0, insertIndex), ...itemsToAdd, ...remainingItems.slice(insertIndex)]

  localSelectedSections.value = []

  // Note: Scroll behavior removed since SectionSelectorSelected is handled externally
}

function removeItems(itemIds?: CanvasRetrieve['id'][]) {
  selectedItems.value = itemIds?.length ? selectedItems.value.filter((item) => !itemIds.includes(item.id)) : []
  localSelectedSections.value = []
}

function toggleSmartFoldersView() {
  isSmartFoldersView.value = !isSmartFoldersView.value

  localSelectedSections.value = []

  // Recalculate height when switching views
  nextTick(() => {
    if (isSmartFoldersView.value) {
      smartFoldersRef.value?.calculateContentHeight?.()
    }
    // Force recalculation of wrapper height
    if (toolbarWrapper.value) {
      const toolbarHeight = toolbarWrapper.value.offsetHeight
      wrapperHeight.value = calculateWrapperHeight(toolbarHeight)
    }
  })
}

function handleSmartFolderSectionSelect(section: CanvasRetrieve) {
  const elementExists = localSelectedSections.value.find((s) => s.id === section.id)
  if (elementExists) {
    localSelectedSections.value = localSelectedSections.value.filter((s) => s.id !== section.id)
  } else {
    localSelectedSections.value.push(section)
  }
}

function handleSmartFolderSectionAdd(section: CanvasRetrieve) {
  addSectionToList([section])
}

function handleSmartFolderSectionRemove(section: CanvasRetrieve) {
  removeItems([section.id])
}

function addSectionToList(sections: CanvasRetrieve[], position?: 'top' | 'bottom', targetIndex?: number) {
  const sectionIds = sections.map((s) => s.id)

  // Separate existing items from new items
  const itemsToMove = addedSections.value.filter((section) => sectionIds.includes(section.id))
  const newItemsToAdd = sections.filter(
    (s) => !itemsToMove.some((m) => m.id === s.id) && !addedSections.value.some((added) => added.id === s.id),
  )

  if (targetIndex === undefined && newItemsToAdd.length === 0 && itemsToMove.length === 0) {
    return
  }

  // Collect all items being added/moved
  let itemsToAdd = sectionIds
    .map((id) => itemsToMove.find((item) => item.id === id) || newItemsToAdd.find((item) => item.id === id))
    .filter((item): item is CanvasRetrieve => item !== undefined)

  // Sort itemsToAdd based on their order in smart folders if sequenceEnabled is true
  if (sequenceEnabled.value && smartFoldersRef.value) {
    itemsToAdd = sortBy(itemsToAdd, (item) => {
      const smartFolderSections = smartFoldersRef.value?.getSelectedSections() || []
      return smartFolderSections.findIndex((section) => section.id === item.id)
    })
  }

  // Work directly with selectedItems - remove the sections being added/moved
  const remainingItems = selectedItems.value.filter((item) => !sectionIds.includes(item.id))

  // Calculate where to insert based on position or targetIndex
  let insertIndex: number

  if (position === 'top') {
    insertIndex = 0
  } else if (targetIndex !== undefined) {
    // Adjust targetIndex for removed items
    let adjustedIndex = targetIndex
    for (let i = 0; i < targetIndex && i < selectedItems.value.length; i++) {
      if (sectionIds.includes(selectedItems.value[i].id)) {
        adjustedIndex--
      }
    }
    insertIndex = Math.max(0, adjustedIndex)
  } else {
    insertIndex = remainingItems.length
  }

  // Insert items at the calculated position
  selectedItems.value = [...remainingItems.slice(0, insertIndex), ...itemsToAdd, ...remainingItems.slice(insertIndex)]

  // Clear selection
  localSelectedSections.value = []

  // Note: Scroll behavior removed since SectionSelectorSelected is handled externally
}

function handleSmartFolderBulkAdd(sections: CanvasRetrieve[], position?: 'top' | 'bottom', targetIndex?: number) {
  addSectionToList(sections, position, targetIndex)
}

function handleSmartFolderBulkRemove(sections: CanvasRetrieve[]) {
  const sectionIds = sections.map((s) => s.id)
  removeItems(sectionIds)
}

function getSectionAllowedIds() {
  const filterSectionIds = getSystemFilterSectionIds()
  const propsAllowedIds = appProps?.allowedIds?.value

  if (!filterSectionIds) return null
  if (propsAllowedIds?.length && filterSectionIds.length) return intersection(propsAllowedIds, filterSectionIds)
  if (!propsAllowedIds && filterSectionIds.length) return filterSectionIds

  return propsAllowedIds || []
}

async function fetchSections() {
  if (!appProps?.listCanvases) return

  const ordering = getDefaultOrdering()
  if (enablePipeMultisearch.value) {
    try {
      isFetchingSections.value = true
      const result = await fetchSectionsWithPipeMultiSearch({
        listCanvases: appProps.listCanvases.value as (params: any) => Promise<PaginatedData<CanvasRetrieve>>,
        search: search.value,
        pipeSearchTerms: pipeSearchTerms.value,
        isPipeSearching: isPipeSearching.value,
        pageSize: PAGE_SIZE.value,
        currentPage: currentPage.value,
        filters: filters.value,
        sectionIds: getSectionAllowedIds(),
        pitcherSettings: pitcherSettings.value,
        restApiAxios: restApiAxios?.value,
      })

      availableSections.value = result.availableSections
      availableSectionsTotalCount.value = result.availableSectionsTotalCount
      firstPageTotalDisplayedSections.value = result.firstPageTotalDisplayedSections
    } catch (e) {
      console.error('Failed to fetch sections ', e)
    } finally {
      isFetchingSections.value = false
    }
    return
  }

  isFetchingSections.value = true
  try {
    availableSections.value = []

    const sectionIds = getSectionAllowedIds()

    // only check for null value not empty array
    if (!sectionIds) {
      availableSectionsTotalCount.value = 0
      isFetchingSections.value = false
      return
    }

    const payload = {
      is_section: true,
      is_template: false,
      is_draft: false,
      ordering,
      page_size: PAGE_SIZE.value,
      search: search.value,
      filters: normalizeFilterParams(filters.value),
    }

    if (sectionIds?.length) {
      const sectionIdsResults = await urlSafeFetchInChunks<Canvas>(
        sectionIds,
        (chunk) =>
          appProps?.listCanvases.value({ ...payload, id__in: chunk.join(','), page_size: chunk.length }) as Promise<{
            results: Canvas[]
          }>,
      )

      firstPageTotalDisplayedSections.value = undefined
      availableSectionsTotalCount.value = sectionIdsResults.length
      availableSections.value = sectionIdsResults
    } else {
      const requestPromises = [
        appProps?.listCanvases.value({
          ...payload,
          page: currentPage.value,
        }),
      ]

      // only show all recommended sections if on the first page
      if (recommendedSectionIds.value?.length && currentPage.value === 1 && !sectionIds?.length)
        requestPromises.push(
          appProps?.listCanvases.value({
            ...payload,
            id__in: recommendedSectionIds.value.join(','),
          }),
        )

      const [sectionsResponse, recommendedSectionsResponse] = await Promise.all(requestPromises)

      if (recommendedSectionsResponse && recommendedSectionsResponse.results?.length) {
        availableSectionsTotalCount.value = sectionsResponse.count!

        const sectionsWithAllRecommendedSections = unionBy(
          sectionsResponse.results,
          recommendedSectionsResponse.results,
          'id',
        )
        firstPageTotalDisplayedSections.value = sectionsWithAllRecommendedSections.length
        availableSections.value = sectionsWithAllRecommendedSections
      } else {
        firstPageTotalDisplayedSections.value = undefined
        availableSectionsTotalCount.value = sectionsResponse.count!
        availableSections.value = sectionsResponse.results
      }
    }
  } catch (error) {
    console.error('Failed to fetch sections', error)
  } finally {
    isFetchingSections.value = false
  }
}

async function fetchSelectedSections() {
  if (!appProps?.listCanvases) return

  const ordering = getDefaultOrdering()
  isFetchingSelectedSections.value = true
  try {
    const selectedSectionIds = selectedSections.value?.map((section) => section.id) || []
    // Split the selectedSectionIds into chunks safe enough for GET URLs
    const maxIdsPerChunk = 100 // Adjust the chunk size to be safe for GET requests
    const idChunks = []
    for (let i = 0; i < selectedSectionIds.length; i += maxIdsPerChunk) {
      idChunks.push(selectedSectionIds.slice(i, i + maxIdsPerChunk))
    }

    // Fetch sections for each chunk and combine the results
    const sectionsPromises = idChunks.map((chunk) =>
      appProps?.listCanvases.value({
        is_section: true,
        is_template: false,
        is_draft: false,
        ordering,
        page_size: PAGE_SIZE.value,
        id__in: chunk.join(','),
      }),
    )
    const sectionsResults = await Promise.all(sectionsPromises)
    const flattenResults = sectionsResults.flatMap((res) => res.results)
    selectedItems.value = sortBy(flattenResults, (value) => selectedSectionIds.indexOf(value.id))
  } catch (e) {
    console.error('Failed to fetch sections', e)
  } finally {
    isFetchingSelectedSections.value = false
  }
}

function fetchNextChunk() {
  currentPage.value++
  fetchSections()
}

function fetchPreviousChunk() {
  if (currentPage.value > 1) {
    currentPage.value--
    fetchSections()
  }
}

function fetchFirstChunk() {
  currentPage.value = 1
  fetchSections()
}

function fetchLastChunk() {
  if (availableSectionsTotalCount.value > 0) {
    currentPage.value = Math.ceil(availableSectionsTotalCount.value / PAGE_SIZE.value)
    fetchSections()
  }
}

function fetchSectionsAfterFiltering() {
  currentPage.value = 1
  fetchSections()
}

function handleSearch(newValue: string) {
  search.value = newValue
  currentPage.value = 1
  fetchSections()
}

function toggleSelectAll() {
  if (launchDarkly.value.enable_selectors_dnd) {
    if (areAllSelected.value) {
      localSelectedSections.value = localSelectedSections.value.filter(
        (s) => !availableSections.value?.some((section) => s.id === section.id),
      )
    } else {
      selectChunk()
    }
  } else {
    if (areAllSelected.value)
      selectedItems.value = selectedItems.value.filter(
        (s) => !availableSections.value?.some((section) => s.id === section.id),
      )
    else selectChunk()
  }
}

// Watch for view mode changes and recalculate height
watch(shouldShowSmartFolderView, () => {
  nextTick(() => {
    if (launchDarkly.value.enable_selectors_dnd && toolbarWrapper.value) {
      const toolbarHeight = toolbarWrapper.value.offsetHeight
      wrapperHeight.value = calculateWrapperHeight(toolbarHeight)
    } else if (!launchDarkly.value.enable_selectors_dnd && toolbarWrapperTraditional.value) {
      const toolbarHeight = toolbarWrapperTraditional.value.offsetHeight
      wrapperHeightTraditional.value = calculateWrapperHeight(toolbarHeight)
    }
  })
})

onMounted(() => {
  filters.value = appProps?.initialMetadataFilters?.value || {}

  if (launchDarkly.value.show_popularity_icons) {
    fetchPopularityData(EntityType.SECTION)
  }
  fetchSections()
  fetchSelectedSections()

  // Add resize observer to handle window resizing
  if (launchDarkly.value.enable_selectors_dnd && toolbarWrapper.value?.parentElement) {
    const resizeObserver = new ResizeObserver(() => {
      nextTick(() => {
        if (toolbarWrapper.value) {
          const toolbarHeight = toolbarWrapper.value.offsetHeight
          wrapperHeight.value = calculateWrapperHeight(toolbarHeight)
        }
      })
    })

    resizeObserver.observe(toolbarWrapper.value.parentElement)
    ;(toolbarWrapper.value.parentElement as any).__resizeObserver = resizeObserver
  } else if (!launchDarkly.value.enable_selectors_dnd && toolbarWrapperTraditional.value?.parentElement) {
    const resizeObserver = new ResizeObserver(() => {
      nextTick(() => {
        if (toolbarWrapperTraditional.value) {
          const toolbarHeight = toolbarWrapperTraditional.value.offsetHeight
          wrapperHeightTraditional.value = calculateWrapperHeight(toolbarHeight)
        }
      })
    })

    resizeObserver.observe(toolbarWrapperTraditional.value.parentElement)
    ;(toolbarWrapperTraditional.value.parentElement as any).__resizeObserver = resizeObserver
  }
})

onBeforeUnmount(() => {
  // Clean up resize observer for both modes
  if (toolbarWrapper.value?.parentElement) {
    const observer = (toolbarWrapper.value.parentElement as any).__resizeObserver
    if (observer) {
      observer.disconnect()
      delete (toolbarWrapper.value.parentElement as any).__resizeObserver
    }
  }
  if (toolbarWrapperTraditional.value?.parentElement) {
    const observer = (toolbarWrapperTraditional.value.parentElement as any).__resizeObserver
    if (observer) {
      observer.disconnect()
      delete (toolbarWrapperTraditional.value.parentElement as any).__resizeObserver
    }
  }
})

// Expose functions for parent components
defineExpose({
  onDrop,
})
</script>
